import React, { useCallback, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { newLogo } from "../../assets/Images";
import facebookIcon from "../../assets/facebook.png";
import linkedinIcon from "../../assets/linkedin.png";
import instagramIcon from "../../assets/instagram.png";
import { useReduxStore } from "../../hooks/useReduxStore";
import { updateHome } from "../../store/Reducers/homeReducer";
import { GetHomeData } from "../../store/Actions/action";
import { scrollToTop } from "../../helper/helper";

export default function FooterNew({
  fbLink,
  instaLink,
  linkdinLink,
  onCategoryPress,
}) {
  const currentYear = new Date().getFullYear();
  const { getState, dispatch } = useReduxStore();
  const { categories } = getState("Home");
  const navigate = useNavigate();
  const hasRequestedRef = useRef(false);

  // Automatically fetch categories if not yet loaded in Redux store
  useEffect(() => {
    if ((!categories || categories.length === 0) && !hasRequestedRef.current) {
      hasRequestedRef.current = true;
      dispatch(GetHomeData());
    }
  }, [categories, dispatch]);

  const onItemClick = useCallback(
    (selectedCategory) => {
      if (typeof onCategoryPress === "function") {
        onCategoryPress(selectedCategory);
      }
      dispatch(updateHome({ selectedCategory }));
      scrollToTop();
      navigate("/ProductCollection", { state: {} });
    },
    [dispatch, navigate, onCategoryPress]
  );

  const isCategoriesLoading =
    (!categories || categories.length === 0) && hasRequestedRef.current;

  return (
    <footer
      className="w-full bg-black text-white border-t border-neutral-900 pt-16 pb-8 px-6 md:px-12 lg:px-20 font-outfit select-none bg-cover bg-center relative"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 pointer-events-none"></div>

      <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
        {/* Left Column: Logo & Socials */}
        <div className="lg:col-span-1.5 flex flex-col items-center md:items-start text-center md:text-left">
          <img
            className="object-contain h-20 md:h-24 w-auto mb-4"
            src={newLogo}
            alt="BAB Logo"
          />

          {/* Social media icons */}
          <div className="flex gap-4">
            <a
              href={
                fbLink ||
                "https://www.facebook.com/people/Boss-Amongst-Bosses-BAB-MEDIA/61567213191170/"
              }
              target="_blank"
              rel="noreferrer"
              className="p-2 bg-neutral-900/60 hover:bg-secondary/20 rounded-lg transition-colors border border-neutral-800"
            >
              <img
                src={facebookIcon}
                alt="Facebook"
                className="w-5 h-5 object-contain"
              />
            </a>
            <a
              href={linkdinLink || "https://www.linkedin.com"}
              target="_blank"
              rel="noreferrer"
              className="p-2 bg-neutral-900/60 hover:bg-secondary/20 rounded-lg transition-colors border border-neutral-800"
            >
              <img
                src={linkedinIcon}
                alt="LinkedIn"
                className="w-5 h-5 object-contain"
              />
            </a>
            <a
              href={instaLink || "https://www.instagram.com/bab_media_"}
              target="_blank"
              rel="noreferrer"
              className="p-2 bg-neutral-900/60 hover:bg-secondary/20 rounded-lg transition-colors border border-neutral-800"
            >
              <img
                src={instagramIcon}
                alt="Instagram"
                className="w-5 h-5 object-contain"
              />
            </a>
          </div>
        </div>

        {/* Column 2: Sub Pages */}
        <div className="text-center md:text-left">
          <h4 className="text-white font-oswald text-sm font-semibold tracking-wider uppercase mb-4">
            Sub pages
          </h4>
          <ul className="space-y-2.5 text-sm text-neutral-400 font-light">
            <li>
              <Link
                to="/digital-library"
                className="hover:text-secondary transition-colors font-medium"
              >
                Digital Library
              </Link>
            </li>
            <li>
              <Link
                to="/soulflow-records"
                className="hover:text-secondary transition-colors font-medium"
              >
                Soul Flow Records
              </Link>
            </li>
            <li>
              <Link
                to="/news-new"
                className="hover:text-secondary transition-colors"
              >
                Media Center
              </Link>
            </li>
            <li>
              <Link
                to="/mystio-new"
                className="hover:text-secondary transition-colors"
              >
                Mystic Global Logistic
              </Link>
            </li>
            <li>
              <Link
                to="/About"
                className="hover:text-secondary transition-colors"
              >
                Our Story
              </Link>
            </li>
            <li>
              <a
                href="https://admin.bab-media.com/public/login"
                target="_blank"
                rel="noreferrer"
                className="hover:text-secondary transition-colors"
              >
                Become a Seller
              </a>
            </li>
          </ul>
        </div>

        {/* Column 3: Our Content (Dynamic Categories) */}
        <div className="text-center md:text-left">
          <h4 className="text-white font-oswald text-sm font-semibold tracking-wider uppercase mb-4">
            Our Content
          </h4>
          {isCategoriesLoading ? (
            <div className="space-y-2.5 py-1" aria-label="Loading categories">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-4 bg-neutral-800/80 rounded w-24 md:w-28 animate-pulse mx-auto md:mx-0"
                />
              ))}
            </div>
          ) : Array.isArray(categories) && categories.length > 0 ? (
            <ul className="space-y-2.5 text-sm text-neutral-400 font-light">
              {categories.map((item, index) => {
                const key = item?.id || item?._id || index;
                return (
                  <li key={key}>
                    <button
                      type="button"
                      onClick={() => onItemClick(item)}
                      className="hover:text-secondary transition-colors cursor-pointer text-center md:text-left inline-block focus:outline-none focus:text-secondary"
                    >
                      {item?.name}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>

        {/* Column 4: Helpful Links */}
        <div className="text-center md:text-left">
          <h4 className="text-white font-oswald text-sm font-semibold tracking-wider uppercase mb-4">
            Helpful Links
          </h4>
          <ul className="space-y-2.5 text-sm text-neutral-400 font-light">
            <li>
              <Link
                to="/FAQ"
                className="hover:text-secondary transition-colors"
              >
                FAQs
              </Link>
            </li>
            <li>
              <Link
                to="/Contact"
                className="hover:text-secondary transition-colors"
              >
                Contact
              </Link>
            </li>
          </ul>
        </div>

        {/* Column 5: Contact Us */}
        <div className="text-center md:text-left">
          <h4 className="text-white font-oswald text-sm font-semibold tracking-wider uppercase mb-4">
            Contact Us
          </h4>
          <ul className="space-y-2.5 text-sm text-neutral-400 font-light">
            <li>
              <a
                href="mailto:babmedia2023@gmail.com"
                className="hover:text-secondary transition-colors"
              >
                babmedia2023@gmail.com
              </a>
            </li>
            <li>
              <a
                href="tel:6097739620"
                className="hover:text-secondary transition-colors"
              >
                609-773-9620
              </a>
            </li>
            <li className="leading-relaxed">Atlantic City, NJ 08401</li>
          </ul>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="relative z-10 max-w-6xl mx-auto border-t border-neutral-900 pt-8 flex flex-col sm:flex-row items-center justify-between text-neutral-500 text-xs sm:text-sm gap-4">
        <div>© {currentYear} Boss Amongst Bosses</div>
        <div className="flex gap-4">
          <span>All rights reserved.</span>
          <Link
            to="/TermsCondition"
            className="text-secondary underline hover:text-secondary/80"
          >
            Terms & Conditions
          </Link>
          <Link
            to="/Policy"
            className="text-secondary underline hover:text-secondary/80"
          >
            Privacy Policy
          </Link>
        </div>
      </div>
    </footer>
  );
}
